import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  FileText, Shield, Eye, Download, Loader2, CheckCircle,
  FileCheck, Phone, Send, AlertCircle, Clock, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';
import { EnvoyerLienSignatureMembre } from '../../components/EnvoyerLienSignatureMembre';

export default function EtapeSignature({
  telephone, setTelephone, codeOtp, setCodeOtp, codeOtpGenere: codeOtpGenere,
  otpEnvoye, setOtpEnvoye, handleDemanderSignature, handleValiderSignature,
  loading, souscriptionId, ftpeStatus, ftpeAdhesionId,
  ftpeTelephone, setFtpeTelephone, ftpeOtpEnvoye, ftpeCodeOtp,
  setFtpeCodeOtp, handleFtpeSendOtp, handleFtpeValidateSignature,
}) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const ftpeSigned = ftpeStatus === 'signed' || ftpeStatus === 'paid' || ftpeStatus === 'validated';

  // Timer SMS
  const [otpTimer, setOtpTimer] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (otpTimer > 0) {
      timerRef.current = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [otpTimer]);

  const startTimer = () => setOtpTimer(600); // 10 min
  const formatTimer = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // OTP input refs for auto-focus
  const otpInputRef = useRef(null);

  const handleSendAndTimer = async () => {
    await handleDemanderSignature();
    startTimer();
    setTimeout(() => otpInputRef.current?.focus(), 300);
  };

  const handleResend = () => {
    setOtpEnvoye(false);
    setCodeOtp('');
    setOtpTimer(0);
  };

  const handleOpenDoc = async (packNum) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/api/souscriptions/${souscriptionId}/download-pack${packNum}?draft=false`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Erreur');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Erreur ouverture document'); }
  };

  const handleDownloadDoc = async (packNum, label) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/api/souscriptions/${souscriptionId}/download-pack${packNum}?draft=false`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Erreur');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${label}_SIGNE_${souscriptionId}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Document signe telecharge');
    } catch { toast.error('Erreur telechargement'); }
  };

  return (
    <div className="space-y-6">
      {/* Documents finaux signés */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden shadow-xl">
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-8 py-6 border-b border-slate-600/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center"><FileCheck className="h-6 w-6 text-emerald-500" /></div>
            <div><h2 className="text-xl font-bold text-white">Pack complet signe</h2><p className="text-slate-400 text-sm">Attestation + CP/CG avec QR code et horodatage</p></div>
          </div>
        </div>
        <div className="p-6 space-y-3">
          {[
            { pack: 1, label: 'ATTESTATION SIGNEE', desc: 'Attestation RCDS + Note de lecture + QR code + horodatage', icon: Shield },
            { pack: 2, label: 'CP / CG SIGNES', desc: 'Conditions Particulieres + Generales + QR code', icon: FileText },
          ].map((doc) => {
            const DocIcon = doc.icon;
            return (
              <div key={doc.pack} className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 hover:border-emerald-500/50 transition-colors" data-testid={`signature-doc-pack${doc.pack}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0"><DocIcon className="h-5 w-5 text-emerald-400" /></div>
                    <div className="min-w-0">
                      <div className="font-semibold text-white text-sm flex items-center gap-2">
                        {doc.label}
                        <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">SIGNE</span>
                      </div>
                      <div className="text-xs text-slate-400">{doc.desc}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => handleOpenDoc(doc.pack)} variant="outline" size="sm" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" data-testid={`signature-view-pack${doc.pack}`}><Eye className="mr-1 h-3 w-3" />Ouvrir</Button>
                    <Button onClick={() => handleDownloadDoc(doc.pack, doc.label.replace(/\s/g, '_'))} size="sm" className="bg-emerald-500/90 hover:bg-emerald-500 text-white" data-testid={`signature-download-pack${doc.pack}`}><Download className="mr-1 h-3 w-3" />Telecharger</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Signature FTPE */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-blue-500/30 rounded-2xl overflow-hidden shadow-xl" data-testid="ftpe-signature-section">
        <div className="bg-gradient-to-r from-blue-900/50 to-indigo-900/50 px-8 py-5 border-b border-blue-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center"><span className="text-blue-400 font-bold">1</span></div>
              <div><h3 className="text-lg font-bold text-white">Signature adhesion FTPE</h3><p className="text-blue-300/70 text-xs">Obligatoire avant la signature RCDS</p></div>
            </div>
            {ftpeSigned && (<div className="flex items-center gap-2 bg-emerald-500/20 px-3 py-1.5 rounded-full"><CheckCircle className="h-4 w-4 text-emerald-400" /><span className="text-emerald-400 text-sm font-medium">Signee</span></div>)}
          </div>
        </div>
        <div className="p-6">
          {ftpeSigned ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <CheckCircle className="h-6 w-6 text-emerald-400" />
                <div className="flex-1"><p className="text-emerald-400 font-medium">Adhesion FTPE signee</p><p className="text-slate-400 text-xs mt-1">Vous pouvez signer la souscription RCDS</p></div>
              </div>
              {ftpeAdhesionId && (
                <div className="bg-slate-900/50 border border-blue-500/30 rounded-xl p-4 hover:border-emerald-500/50 transition-colors" data-testid="ftpe-final-doc-card">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0"><FileCheck className="h-5 w-5 text-blue-400" /></div>
                      <div className="min-w-0"><div className="font-semibold text-white text-sm flex items-center gap-2">BULLETIN ADHESION FTPE<span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">SIGNE</span></div><div className="text-xs text-slate-400">Version signee avec QR code</div></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={async () => { try { const token = getToken(); const res = await fetch(`${API}/api/ftpe-adhesion/workflow/${ftpeAdhesionId}/document?draft=false`, { headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error(); window.open(URL.createObjectURL(await res.blob()), '_blank'); } catch { toast.error('Erreur ouverture'); } }} variant="outline" size="sm" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10" data-testid="ftpe-final-view"><Eye className="mr-1 h-3 w-3" />Ouvrir</Button>
                      <Button onClick={async () => { try { const token = getToken(); const res = await fetch(`${API}/api/ftpe-adhesion/workflow/${ftpeAdhesionId}/document?draft=false`, { headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error(); const url = URL.createObjectURL(await res.blob()); const a = document.createElement('a'); a.href = url; a.download = `BULLETIN_FTPE_SIGNE_${ftpeAdhesionId}.pdf`; document.body.appendChild(a); a.click(); document.body.removeChild(a); toast.success('Telecharge'); } catch { toast.error('Erreur'); } }} size="sm" className="bg-emerald-500/90 hover:bg-emerald-500 text-white" data-testid="ftpe-final-download"><Download className="mr-1 h-3 w-3" />Telecharger</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {!ftpeOtpEnvoye ? (
                <>
                  <p className="text-slate-300 text-sm">Entrez votre numero de telephone pour recevoir le code FTPE :</p>
                  <div className="flex gap-3">
                    <Input type="tel" value={ftpeTelephone} onChange={(e) => setFtpeTelephone(e.target.value)} placeholder="06 XX XX XX XX" className="bg-slate-900 border-slate-600 text-white flex-1" data-testid="ftpe-phone-input" />
                    <Button onClick={handleFtpeSendOtp} disabled={loading} className="bg-blue-600 hover:bg-blue-500" data-testid="ftpe-send-otp">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Envoyer le code'}</Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-300 text-sm">Entrez le code recu par SMS pour signer l'adhesion FTPE :</p>
                  <div className="flex gap-3">
                    <Input type="text" value={ftpeCodeOtp} onChange={(e) => setFtpeCodeOtp(e.target.value)} placeholder="Code OTP" maxLength={6} className="bg-slate-900 border-slate-600 text-white flex-1" data-testid="ftpe-otp-input" />
                    <Button onClick={handleFtpeValidateSignature} disabled={loading} className="bg-blue-600 hover:bg-blue-500" data-testid="ftpe-validate-otp">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Valider signature FTPE'}</Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Signature RCDS */}
      <div className={`bg-slate-800/50 backdrop-blur-sm border ${ftpeSigned ? 'border-slate-700/50' : 'border-slate-700/20 opacity-60'} rounded-2xl overflow-hidden shadow-xl transition-all`} data-testid="rcds-signature-section">
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-8 py-6 border-b border-slate-600/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center"><span className="text-amber-500 font-bold text-lg">2</span></div>
            <div><h2 className="text-xl font-bold text-white">Signature RCDS</h2><p className="text-slate-400 text-sm">{ftpeSigned ? 'Validez votre souscription par SMS' : "Signez d'abord l'adhesion FTPE ci-dessus"}</p></div>
          </div>
        </div>
        <div className="p-8 space-y-6">
          {!ftpeSigned && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0" />
              <p className="text-amber-400 text-sm">Vous devez d'abord signer l'adhesion FTPE avant de pouvoir signer la souscription RCDS.</p>
            </div>
          )}
          {ftpeSigned && !otpEnvoye ? (
            <>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start space-x-3">
                <Phone className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-blue-400 font-medium">Verification par SMS</p>
                  <p className="text-slate-400 text-sm">Un code a 6 chiffres sera envoye pour signer l'ensemble du pack (Attestation + CP/CG).</p>
                </div>
              </div>
              <div>
                <Label htmlFor="telephone" className="text-slate-300">Numero de telephone</Label>
                <Input id="telephone" type="tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="+33 6 12 34 56 78"
                  className="mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500 text-lg h-14" data-testid="rcds-phone-input" />
              </div>
              <Button onClick={handleSendAndTimer} disabled={loading || !telephone.trim()}
                className="w-full h-14 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold text-lg shadow-lg shadow-amber-500/20" data-testid="rcds-send-otp">
                {loading ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Envoi en cours...</>) : (<><Send className="mr-2 h-5 w-5" />Envoyer le code de signature</>)}
              </Button>
            </>
          ) : ftpeSigned ? (
            <div className="space-y-5">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-emerald-400 font-medium">Code envoye au {telephone?.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')}</p>
                  {otpTimer > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <Clock className="h-3.5 w-3.5 text-amber-400" />
                      <span className="text-amber-400 text-xs font-mono">{formatTimer(otpTimer)}</span>
                      <span className="text-slate-500 text-xs">restantes</span>
                    </div>
                  )}
                  {otpTimer === 0 && otpEnvoye && (
                    <p className="text-red-400 text-xs mt-1">Code expire — renvoyez un nouveau code</p>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="code_otp" className="text-slate-300 flex items-center justify-between">
                  <span>Code de signature (6 chiffres)</span>
                  {otpTimer > 0 && <span className="text-xs text-slate-500 font-normal">Verifiez vos SMS</span>}
                </Label>
                <Input ref={otpInputRef} id="code_otp" type="text" inputMode="numeric" value={codeOtp}
                  onChange={(e) => setCodeOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="------" maxLength={6}
                  className="mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500 text-center text-2xl tracking-[0.5em] h-16 font-mono" data-testid="rcds-otp-input" />
              </div>
              <div className="flex gap-3">
                <Button onClick={handleValiderSignature} disabled={loading || codeOtp.length !== 6}
                  className="flex-1 h-14 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold text-lg shadow-lg shadow-amber-500/20 disabled:opacity-50" data-testid="rcds-validate-otp">
                  {loading ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Signature en cours...</>) : (<><Shield className="mr-2 h-5 w-5" />Signer le pack complet</>)}
                </Button>
                <Button onClick={handleResend} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 gap-1.5" data-testid="rcds-resend-otp">
                  <RefreshCw className="h-4 w-4" />Renvoyer
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <EnvoyerLienSignatureMembre souscriptionId={souscriptionId} telephone={telephone || ftpeTelephone} ftpeAdhesionId={ftpeAdhesionId} className="mt-6" />
    </div>
  );
}
